/* App — el armazón: rutas, sesión y qué se ve mientras no la hay.
 *
 * La ruta raíz es la BANDEJA, no el asistente. Es el cambio de jerarquía del rediseño: quien
 * entra a Vigía viene a decidir dónde presentar oferta; preguntarle al asistente es una acción
 * dentro de ese trabajo, no la puerta.
 */
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import type { Capacidad } from './api/tipos'
import { ProveedorSesion, useSesion } from './estado/sesion'
import { nombreEmpresa, ProveedorPerfil, usePerfil } from './estado/perfil'
import { Masthead } from './ui/Masthead'
import { Bandeja } from './paginas/Bandeja'
import { Ficha } from './paginas/Ficha'
import { Pliego } from './paginas/Pliego'
import { Asistente } from './paginas/Asistente'
import { PerfilPagina } from './paginas/Perfil'
import { Empresas } from './paginas/Empresas'
import { Documentos } from './paginas/Documentos'
import { Primeros, necesitaPrimeros } from './paginas/Primeros'
import { Administracion } from './paginas/Administracion'
import { Ingreso } from './paginas/Ingreso'
import './estilos/fuentes.css'
import './estilos/fundamentos.css'
import './estilos/base.css'
import './App.css'

export function App() {
  return (
    <BrowserRouter>
      <ProveedorSesion>
        <Rutas />
      </ProveedorSesion>
    </BrowserRouter>
  )
}

function Rutas() {
  return (
    <Routes>
      <Route path="/ingresar" element={<Ingreso />} />
      <Route
        path="/*"
        element={
          <ConSesion>
            <ProveedorPerfil>
              <Marco>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <ConCapacidad cap="panel">
                        <Bandeja />
                      </ConCapacidad>
                    }
                  />
                  <Route
                    path="/proceso/:id"
                    element={
                      <ConCapacidad cap="panel">
                        <Ficha />
                      </ConCapacidad>
                    }
                  />
                  <Route
                    path="/proceso/:id/pliego"
                    element={
                      <ConCapacidad cap="analizar">
                        <Pliego />
                      </ConCapacidad>
                    }
                  />
                  <Route
                    path="/asistente"
                    element={
                      <ConCapacidad cap="chat">
                        <Asistente />
                      </ConCapacidad>
                    }
                  />
                  <Route path="/perfil" element={<PerfilPagina />} />
                  <Route path="/empresas" element={<Empresas />} />
                  <Route path="/proceso/:id/documentos" element={<Documentos />} />
                  {/* Fuera de `Marco` no puede ir: necesita el perfil. Y `Marco` lo deja pasar
                      porque su propia condición excluye esta ruta. */}
                  <Route path="/primeros" element={<Primeros />} />
                  {/* Tras `es_admin`, que es un ROL: `ConCapacidad` no sirve, porque las
                      capacidades se reparten desde esta misma pantalla. La comprobación de verdad
                      está en el servidor; esta solo evita enseñar una pantalla que va a dar 403. */}
                  <Route path="/administracion" element={<Administracion />} />
                  <Route path="*" element={<NoEncontrado />} />
                </Routes>
              </Marco>
            </ProveedorPerfil>
          </ConSesion>
        }
      />
    </Routes>
  )
}

/** Puerta de sesión. Guarda a dónde iba para volver ahí tras entrar: mandar a todo el mundo a la
 *  bandeja tras el ingreso pierde el enlace a una ficha compartida por correo. */
function ConSesion({ children }: { children: ReactNode }) {
  const { yo, cargando, error } = useSesion()
  const ubicacion = useLocation()

  if (cargando) return <Cargando />
  if (error) return <FalloDeArranque mensaje={error} />
  // Con la clave temporal sin cambiar, el servidor responde 403 a todo lo demás: dejar ver la
  // aplicación por detrás solo produciría una pantalla de errores.
  if (!yo || yo.cambiar_clave) {
    return (
      <Navigate
        to="/ingresar"
        replace
        state={{ volverA: ubicacion.pathname + ubicacion.search }}
      />
    )
  }
  return <>{children}</>
}

/** Puerta de capacidad. Esconder la pestaña no basta: con la URL a mano se llega igual, y lo que
 *  se ve es una pantalla cuyas peticiones responden todas 403. Es COMODIDAD, no seguridad —cada
 *  endpoint verifica por su cuenta—, pero sin ella la interfaz miente sobre lo que ofrece. */
function ConCapacidad({ cap, children }: { cap: Capacidad; children: ReactNode }) {
  const { puede } = useSesion()
  if (puede(cap)) return <>{children}</>
  return (
    <div className="andamio">
      <h1>Esta sección no está en tu cuenta</h1>
      <p>
        Si crees que debería estarlo, pídeselo a quien administra Vigía.
        {cap !== 'panel' && (
          <>
            {' '}
            <a href="/">Volver a las oportunidades</a>
          </>
        )}
      </p>
    </div>
  )
}

/**
 * El marco de la aplicación… salvo cuando la cuenta está VACÍA.
 *
 * Sin perfil, la bandeja ordena por criterios generales y la columna de afinidad sale en blanco:
 * quien entra por primera vez ve una lista que no le habla y no tiene por qué adivinar que hay que
 * ir a «Mi perfil» a cargar un RUP. Por eso los primeros pasos se ATRAVIESAN, una vez.
 *
 * ⚠ Solo en una cuenta vacía, y con «saltar por ahora» a la vista. A quien ya usa la aplicación no
 * se le atraviesa nada, ni el día del despliegue: `necesitaPrimeros` mira que no haya perfil
 * guardado NI rastro de uno. Y no se pinta mientras el perfil está cargando, porque en ese momento
 * todavía parece vacío — sería un onboarding que aparece medio segundo y desaparece.
 */
function Marco({ children }: { children: ReactNode }) {
  const { perfil, rup, existe, estado } = usePerfil()
  const ubicacion = useLocation()
  if (estado !== 'cargando' && necesitaPrimeros(perfil, existe)
      && ubicacion.pathname !== '/primeros') {
    return <Navigate to="/primeros" replace />
  }
  // Los primeros pasos van SIN masthead: son un flujo, no una sección. Con la barra puesta
  // invitarían a irse a otro sitio a mitad, que es justo lo que «saltar por ahora» ya ofrece de
  // forma explícita y reversible.
  if (ubicacion.pathname === '/primeros') return <main className="marco">{children}</main>
  return (
    <>
      <Masthead empresa={nombreEmpresa(perfil, rup)} conPerfil={existe} />
      <main className="marco">{children}</main>
    </>
  )
}

function Cargando() {
  return (
    <div className="estado-pagina">
      <p className="rotulo">Cargando</p>
    </div>
  )
}

function FalloDeArranque({ mensaje }: { mensaje: string }) {
  return (
    <div className="estado-pagina">
      <h1>No pude cargar Vigía</h1>
      <p>{mensaje}</p>
      <p className="pista">
        Si acabas de publicar, revisa que <code>PUBLIC_ORIGIN</code> y <code>COOKIE_DOMAIN</code>{' '}
        apunten a este dominio: un desajuste ahí se ve exactamente así.
      </p>
    </div>
  )
}

function NoEncontrado() {
  return (
    <div className="estado-pagina">
      <h1>Esa página no existe</h1>
      <p>
        Puede que el enlace esté incompleto. <a href="/">Volver a las oportunidades</a>
      </p>
    </div>
  )
}
